import React from 'react';
import { motion } from 'framer-motion';
import { ShieldOff, BadgeCheck, Rocket, Swords, HeartHandshake, Mic, GraduationCap, Users, LayoutDashboard, Wrench, ScanSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * WaitlistFeatureShowcase
 * 
 * Extracted from WaitlistPage.tsx — contains:
 *   - Definition section ("What is a Replica")
 *   - The Stand (Value Proposition with 4-card grid)
 *   - Already Built (Product proof with 6 items)
 *   - What Makes It Different
 * 
 * Pure presentational — no state dependencies from parent.
 */
const WaitlistFeatureShowcase: React.FC = () => {
    const { t } = useTranslation('marketing');

    return (
        <>
            {/* ═══════════ WHAT IS A REPLICA (Definition) ═══════════ */}
            <section className="relative py-14 md:py-20" id="definition">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-2xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-6 tracking-tight"
                        >
                            {t('waitlist.definition_title')}
                        </motion.h2>
                        <p className="text-sm md:text-base text-hig-text-secondary leading-relaxed mb-5">
                            {t('waitlist.definition_body')}
                        </p>
                        <p className="text-sm md:text-base text-hig-text-secondary leading-relaxed mb-8 font-medium">
                            {t('waitlist.definition_point')}
                        </p>

                        {/* 3-column grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[1, 2, 3].map((col) => (
                                <motion.div
                                    key={col}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-40px' }}
                                    transition={{ duration: 0.5, delay: col * 0.1 }}
                                    className="p-5 rounded-xl border border-hig-border bg-hig-bg-tertiary/30 hover:border-hig-border/80 transition-colors duration-300"
                                >
                                    <h3 className="text-sm font-semibold text-hig-text-primary mb-2">
                                        {t(`waitlist.definition_col${col}_title`)}
                                    </h3>
                                    <p className="text-[11px] text-hig-text-tertiary leading-relaxed">
                                        {t(`waitlist.definition_col${col}_desc`)}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════ THE STAND. Value Proposition ═══════════ */}
            <section className="relative py-14 md:py-20" id="the-stand">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                {/* Subtle background glow */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#00E5FF] rounded-full filter blur-[200px] opacity-[0.06]" />
                </div>
                <div className="container-hig relative">
                    <div className="max-w-3xl mx-auto">
                        {/* Eyebrow */}
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.5 }}
                            className="text-[10px] font-bold tracking-[0.25em] uppercase text-hig-accent/70 mb-5 text-center"
                        >
                            {t('waitlist.value_stand_eyebrow')}
                        </motion.p>

                        {/* Title */}
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl lg:text-4xl font-semibold text-hig-text-primary mb-5 tracking-tight text-center"
                        >
                            {t('waitlist.value_stand_title')}
                        </motion.h2>

                        {/* Subtitle */}
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-sm md:text-base text-hig-text-tertiary leading-relaxed text-center max-w-2xl mx-auto mb-10"
                        >
                            {t('waitlist.value_stand_subtitle')}
                        </motion.p>

                        {/* 4-Card Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {([
                                { icon: ShieldOff, titleKey: 'value_stand_card1_title', descKey: 'value_stand_card1_desc', badgeKey: 'value_stand_card1_badge', color: '#22d3ee', gradFrom: 'rgba(34,211,238,0.25)', gradTo: 'rgba(34,211,238,0.05)' },
                                { icon: BadgeCheck, titleKey: 'value_stand_card2_title', descKey: 'value_stand_card2_desc', badgeKey: 'value_stand_card2_badge', color: '#fbbf24', gradFrom: 'rgba(251,191,36,0.25)', gradTo: 'rgba(251,191,36,0.05)' },
                                { icon: Rocket, titleKey: 'value_stand_card3_title', descKey: 'value_stand_card3_desc', badgeKey: 'value_stand_card3_badge', color: '#34d399', gradFrom: 'rgba(52,211,153,0.25)', gradTo: 'rgba(52,211,153,0.05)' },
                                { icon: Swords, titleKey: 'value_stand_card4_title', descKey: 'value_stand_card4_desc', badgeKey: 'value_stand_card4_badge', color: '#f43f5e', gradFrom: 'rgba(244,63,94,0.25)', gradTo: 'rgba(244,63,94,0.05)' },
                            ] as const).map((card, i) => {
                                const IconComp = card.icon;
                                return (
                                    <motion.div
                                        key={card.titleKey}
                                        initial={{ opacity: 0, y: 18 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        transition={{ duration: 0.5, delay: i * 0.1 }}
                                        className="group relative p-6 rounded-2xl border border-hig-border bg-hig-bg-tertiary/20 hover:bg-hig-bg-tertiary/40 transition-all duration-500 overflow-hidden"
                                    >
                                        {/* Hover glow */}
                                        <div
                                            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                                            style={{ background: `radial-gradient(ellipse at 30% 20%, ${card.gradFrom}, ${card.gradTo}, transparent 70%)` }}
                                        />
                                        {/* Top accent line */}
                                        <div
                                            className="absolute top-0 inset-x-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                            style={{ background: `linear-gradient(90deg, transparent, ${card.color}60, transparent)` }}
                                        />

                                        <div className="relative">
                                            {/* Badge */}
                                            <div className="flex items-center gap-2 mb-4">
                                                <div
                                                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                                                    style={{ background: `${card.color}15`, border: `1px solid ${card.color}30` }}
                                                >
                                                    <IconComp className="w-4.5 h-4.5" style={{ color: card.color }} />
                                                </div>
                                                <span
                                                    className="text-[9px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full"
                                                    style={{ background: `${card.color}12`, color: `${card.color}`, border: `1px solid ${card.color}25` }}
                                                >
                                                    {t(`waitlist.${card.badgeKey}`)}
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h3 className="text-base font-semibold text-hig-text-primary mb-3 leading-tight">
                                                {t(`waitlist.${card.titleKey}`)}
                                            </h3>

                                            {/* Description */}
                                            <p className="text-[12px] text-hig-text-tertiary leading-relaxed">
                                                {t(`waitlist.${card.descKey}`)}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Bottom ethical statement - full width */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-40px' }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-8 relative rounded-2xl border border-hig-border bg-hig-bg-tertiary/30 p-7 overflow-hidden"
                        >
                            {/* Animated accent border top */}
                            <div
                                className="absolute top-0 inset-x-0 h-[2px]"
                                style={{ background: 'linear-gradient(90deg, var(--accent), #a78bfa, #f43f5e, #fbbf24, var(--accent))' }}
                            />
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-hig-bg-tertiary/50 border border-hig-border">
                                    <HeartHandshake className="w-5 h-5 text-hig-text-secondary" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-hig-text-primary mb-2">
                                        {t('waitlist.value_stand_bottom_title')}
                                    </h3>
                                    <p className="text-[12px] text-hig-text-tertiary leading-relaxed mb-4">
                                        {t('waitlist.value_stand_bottom_desc')}
                                    </p>
                                    <p className="text-sm text-hig-accent/60 font-semibold italic">
                                        {t('waitlist.value_stand_closing')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>


            {/* ═══════════ ALREADY BUILT. Product Proof ═══════════ */}
            <section className="relative py-14 md:py-20" id="already-built">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-3xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-3 tracking-tight text-center"
                        >
                            {t('waitlist.already_built_title')}
                        </motion.h2>
                        <p className="text-sm text-hig-text-tertiary text-center mb-8 max-w-lg mx-auto">
                            {t('waitlist.already_built_subtitle')}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {[
                                { icon: Mic, key: 1, color: '#f43f5e' },
                                { icon: GraduationCap, key: 2, color: '#a78bfa' },
                                { icon: Users, key: 3, color: '#34d399' },
                                { icon: LayoutDashboard, key: 4, color: '#00E5FF' },
                                { icon: Wrench, key: 5, color: '#fbbf24' },
                                { icon: ScanSearch, key: 6, color: '#22d3ee' },
                            ].map((item, i) => {
                                const IconComp = item.icon;
                                return (
                                    <motion.div
                                        key={item.key}
                                        initial={{ opacity: 0, y: 14 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: '-40px' }}
                                        transition={{ duration: 0.45, delay: i * 0.06 }}
                                        className="group relative flex flex-col gap-3 p-5 rounded-xl border border-hig-border bg-hig-bg-tertiary/30 hover:bg-hig-bg-tertiary/40 hover:border-hig-border/80 transition-all duration-300"
                                    >
                                        <div
                                            className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                            style={{ background: `radial-gradient(circle at 30% 30%, ${item.color}10, transparent 70%)` }}
                                        />
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                                style={{ background: `${item.color}18`, border: `1px solid ${item.color}35` }}
                                            >
                                                <IconComp className="w-4 h-4" style={{ color: item.color }} />
                                            </div>
                                            <p className="text-[12px] font-semibold text-hig-text-secondary leading-tight">
                                                {t(`waitlist.already_built_${item.key}_title`)}
                                            </p>
                                        </div>
                                        <p className="text-[10px] text-hig-text-tertiary leading-relaxed">
                                            {t(`waitlist.already_built_${item.key}_desc`)}
                                        </p>
                                        <div className="flex items-center gap-1.5 mt-auto">
                                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                            <span className="text-[9px] text-green-400/60 font-medium tracking-wider uppercase">Live</span>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════ WHAT MAKES IT DIFFERENT ═══════════ */}
            <section className="relative py-14 md:py-20" id="difference">
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-border to-transparent" />
                <div className="container-hig">
                    <div className="max-w-2xl mx-auto">
                        <motion.h2
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6 }}
                            className="text-2xl md:text-3xl font-semibold text-hig-text-primary mb-6 tracking-tight"
                        >
                            {t('waitlist.difference_title')}
                        </motion.h2>
                        <div className="space-y-5">
                            <p className="text-sm text-hig-text-tertiary leading-relaxed">{t('waitlist.difference_p1')}</p>
                            <p className="text-base md:text-lg text-hig-text-secondary leading-relaxed font-medium">{t('waitlist.difference_p2')}</p>
                            <p className="text-sm text-hig-text-tertiary leading-relaxed">{t('waitlist.difference_p3')}</p>
                        </div>

                        <div className="mt-8 space-y-2">
                            {[1, 2, 3].map((n) => (
                                <p key={n} className="text-[11px] text-hig-text-tertiary flex items-center gap-2">
                                    <span className="text-hig-text-tertiary"></span>
                                    {t(`waitlist.difference_bullet${n}`)}
                                </p>
                            ))}
                            <p className="text-sm text-hig-accent/50 font-semibold mt-4">
                                {t('waitlist.difference_summary')}
                            </p>
                        </div>

                        {/* Practical example */}
                        <div className="mt-10 p-5 rounded-xl border border-hig-border bg-hig-bg-tertiary/30">
                            <p className="text-xs text-hig-text-tertiary leading-relaxed italic">
                                {t('waitlist.difference_example')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default WaitlistFeatureShowcase;
